import { useEffect, useMemo, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import styles from "@/components/todos/todoImageThumbnails.module.css";
import { getImageUrl } from "@/helper/imageUrlHandler";
import { getCachedObjectUrl, prefetchImage } from "@/helper/imageCache";

interface TodoImageThumbnailsProps {
  images: Array<{
    id: string;
    name: string;
    view_link: string;
    download_link: string;
    extension: string;
  }>;
  onImageClick: (imageIndex: number) => void;
  childrenSubjectId?: string;
  customThumbnailimageAlt?: string;
}

export default function TodoImageThumbnails({
  images,
  onImageClick,
  childrenSubjectId,
  customThumbnailimageAlt
}: TodoImageThumbnailsProps) {
  const { t } = useTranslation();

  const [imageLoadingStates, setImageLoadingStates] = useState<Record<string, boolean>>({});
  const [objectUrlMap, setObjectUrlMap] = useState<Record<string, string>>({});

  if (!images || images.length === 0) {
    return null;
  }

  const handleImageLoad = (imageId: string) => {
    setImageLoadingStates(prev => ({
      ...prev,
      [imageId]: false
    }));
  };

  const handleImageError = (imageId: string) => {
    setImageLoadingStates(prev => ({
      ...prev,
      [imageId]: false
    }));
  };

  const handleThumbnailClick = (imageIndex: number) => {
    onImageClick(imageIndex);
  };

  const resolvedViewUrls = useMemo(() => {
    return images.map((img) => ({ id: img.id, url: getImageUrl(img.view_link, childrenSubjectId) }));
  }, [images, childrenSubjectId]);

  useEffect(() => {
    let isMounted = true;

    const initialStates: Record<string, boolean> = {};
    images.forEach((img) => {
      initialStates[img.id] = getCachedObjectUrl(getImageUrl(img.view_link, childrenSubjectId))
        ? false
        : true;
    });
    setImageLoadingStates(initialStates);

    Promise.allSettled(
      resolvedViewUrls.map(({ id, url }) =>
        prefetchImage(url)
          .then((objectUrl) => ({ id, objectUrl }))
      )
    ).then((results) => {
      if (!isMounted) return;
      const nextMap: Record<string, string> = {};
      const nextStates: Record<string, boolean> = {};
      results.forEach((res, idx) => {
        const { id } = resolvedViewUrls[idx];
        if (res.status === "fulfilled") {
          nextMap[id] = res.value.objectUrl;
          nextStates[id] = false;
        } else {
          nextStates[id] = false;
        }
      });
      setObjectUrlMap((prev) => ({ ...prev, ...nextMap }));
      setImageLoadingStates((prev) => ({ ...prev, ...nextStates }));
    });

    return () => {
      isMounted = false;
    };
  }, [resolvedViewUrls, images, childrenSubjectId]);

  return (
    <Box>
      <Box className={styles.headerWrapper}>
        <PhotoLibraryIcon className={styles.todoImagesIcon} />
        <Typography variant="body2" className={styles.todoImagesTitle}>
          {t("images")} ({images.length})
        </Typography>
      </Box>
      <Box className={styles.todoImagesGrid}>
        {images.map((image, index) => (
          <Box
            key={image.id}
            className={styles.todoImageThumbnail}
            onClick={() => handleThumbnailClick(index)}
          >
            {imageLoadingStates[image.id] !== false && (
              <Box className={styles.todoImageLoading}>
                <CircularProgress
                  size={24}
                  thickness={3}
                  sx={{ color: "rgba(0, 0, 0, 0.3)" }}
                />
              </Box>
            )}
            <img
              src={objectUrlMap[image.id] || undefined}
              alt={customThumbnailimageAlt ? customThumbnailimageAlt : image.name}
              className={styles.todoImageThumbnailImage}
              onLoad={() => handleImageLoad(image.id)}
              onError={() => handleImageError(image.id)}
              style={{
                display: imageLoadingStates[image.id] === false ? "block" : "none"
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
}
